import React, {useEffect, useState} from "react";
import {convertMicrView, ConvertToText, ReviewStateEnum} from "../services/Utils";
import {DataTable} from "../components/DataTable";
import {SelectedCheck} from "../components/SelectedCheck";
import {useGlobalContext} from "../context/GlobalState";
import {useSearchParams} from "react-router-dom";
import {useSignalR} from "../hooks/useSignalR";
// MUI Imports
import {Box, Tab, Tabs, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Grid from '@mui/material/Grid2';

// Tab panel component to display the correct tab
function TabPanel(props) {
    const {children, value, index} = props;
    return <div>{value === index && children}</div>;
}

// Deposit review page component
export const DepositReview = () => {
    const theme = useTheme();
    const [selectedCheck, setSelectedCheck] = useState(null);
    const [sortedIds, setSortedIds] = useState([]);
    const {checkData, tabStatus, handleTabStatus} = useGlobalContext();
    const [lockedCheckIdArray, setLockedCheckIdArray] = useState([]);
    const {connectionId, emitLockElement} = useSignalR(selectedCheck, setSelectedCheck, setLockedCheckIdArray);
    const [filterParams, setFilterParams] = useState([]);
    let [searchParams] = useSearchParams();

    useEffect(() => {
        const allParams = Object.fromEntries(searchParams.entries());
        if (allParams.value) {
            allParams.value = allParams.value.includes(",") ? allParams.value.split(",") : allParams.value;
        }
        setFilterParams(allParams);
    }, [searchParams]);
    
    useEffect(() => {
        setSelectedCheckLock(selectedCheck);
    }, [lockedCheckIdArray]);

    useEffect(() => {
        if (selectedCheck?.checkID && connectionId) {
            emitLockElement(selectedCheck.checkID);
        }
    }, [selectedCheck?.checkID, connectionId]);
    
    // Gets selected check from the data table and sets it to the selectedCheck state to load in the SelectedCheck component with all transaction data
    const getSelectedCheck = (selection) => {
        setSelectedCheck(checkData.find(transaction => transaction.checkID === selection));
    };

    const setSelectedCheckLock = (selectedCheck) => {
        if (lockedCheckIdArray && selectedCheck && selectedCheck.checkID && lockedCheckIdArray.length > 0) {
            selectedCheck.locked = lockedCheckIdArray.map(lock => lock.checkID).includes(selectedCheck.checkID);
        }
    };

    const updateSortedIds = (newSort) => {
        setSortedIds(newSort);
    };

    function titleWithCount(title, rows, outlineColor) {
        return (
            <Box
                sx={{
                    margin: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Typography sx={{fontSize: "fontSize", fontWeight: "fontWeightBold"}}>
                    {title}
                </Typography>
                <Typography
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "primary.background",
                        color: "primary.main",
                        borderRadius: 1,
                        width: "fit-content",
                        minWidth: "30px",
                        height: "30px",
                        px: 1,
                        ml: 1,
                        fontSize: "fontSize",
                        border: "1px solid",
                        borderColor: outlineColor,
                    }}
                >
                    {rows ? rows.length : 0}
                </Typography>
            </Box>
        );
    }
    
    let rowData = checkData?.map((transaction) => ({
        item: transaction.checkID,
        date: new Date(`${transaction.date}Z`), // 'Z' denotes UTC for correct conversion to local user's time
        terminalId: transaction.terminalId,
        branch: transaction.branch,
        micr: convertMicrView(ConvertToText(transaction.micr)),
        session: transaction.sessionNumber,
        accountNumber: transaction.accountNumber,
        itemAmount: transaction.itemAmount,
        itemType: transaction.itemType,
        reviewState: transaction.reviewState,
        reviewTime: new Date(`${transaction.reviewTime}Z`),
        rejectReason: transaction.rejectReason,
        primaryReviewer: transaction.primaryReviewer,
        truechecksRecommendedAction: transaction.truechecksRecommendedAction,
        truechecksRecommendedReason: transaction.truechecksRecommendedReason,
        locked: lockedCheckIdArray?.map(lock => lock.checkID).includes(transaction.checkID),
        validMicr: transaction.validMicr,
        reviewStateId: transaction.reviewStateId
    }));
    
    // Sets rows array to null if rowData is null to prevent errors
    let {
        allTransactions = [],
        pendingRows = [],
        approvedRows = [],
        rejectedRows = [],
    } = {
        allTransactions: rowData,
        pendingRows: rowData?.filter(
            (transaction) => transaction.reviewStateId === ReviewStateEnum.Pending
        ),
        approvedRows: rowData?.filter(
            (transaction) => transaction.reviewStateId === ReviewStateEnum.Approved
        ),
        rejectedRows: rowData?.filter(
            (transaction) => transaction.reviewStateId === ReviewStateEnum.Rejected
        ),
    };

    return (
        <>
            <title>Deposit Review</title>
            {/* Creates two separate Grid boxes for singular check info and check data table */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateRows: "auto 1fr",
                    background: "transparent",
                    width: "100%",
                    height: "100%",
                    padding: 0,
                    rowGap: 1,
                }}
            >
                {/* Single check info */}
                <Grid
                    sx={{
                        width: "auto",
                        borderRadius: 1,
                        backgroundColor: "primary.componentbg",
                        padding: 2,
                    }}
                >
                    {/* Gets SelectedCheck.jsx component to display on screen */}
                    <SelectedCheck
                        selectedCheck={selectedCheck}
                        setSelectedCheck={setSelectedCheck}
                        sortedCheckIds={sortedIds}
                        connectionId={connectionId}
                    />
                </Grid>

                {/* Data table component for checks */}
                <Grid
                    sx={{
                        width: "auto",
                        backgroundColor: "primary.componentbg",
                        padding: 2,
                        borderRadius: 1,
                        height: 'auto',
                    }}
                >
                    <Tabs
                        value={tabStatus}
                        onChange={handleTabStatus}
                        indicatorColor="primary"
                        aria-label="secondary tabs example"
                    >
                        <Tab
                            value={"Pending"}
                            label={titleWithCount("Pending", pendingRows, theme.palette.primary.main)}
                        />
                        <Tab
                            value={"Approved"}
                            label={titleWithCount("Approved", approvedRows, theme.palette.accent.success)}
                        />
                        <Tab
                            value={"Rejected"}
                            label={titleWithCount("Rejected", rejectedRows, theme.palette.accent.error)}
                        />
                        <Tab
                            value={"All"}
                            label={titleWithCount(
                                "All Transactions",
                                allTransactions,
                                theme.palette.gray.main
                            )}
                        />
                    </Tabs>
                    {/* Tab For Pending Transactions */}
                    <TabPanel value={tabStatus} index={"Pending"}>
                        {/* Pending Transactions imported from DataTable.jsx */}
                        <DataTable
                            tabStatus={tabStatus}
                            rows={pendingRows}
                            getSelectedCheck={getSelectedCheck}
                            showLockedColumn={true}
                            updateSortedIds={updateSortedIds}
                            selectedCheckId={selectedCheck?.checkID}
                            filterParams={filterParams}
                        />
                    </TabPanel>
                    <TabPanel value={tabStatus} index={"Approved"}>
                        <DataTable
                            tabStatus={tabStatus}
                            rows={approvedRows}
                            getSelectedCheck={getSelectedCheck}
                            showLockedColumn={false}
                            updateSortedIds={updateSortedIds}
                            selectedCheckId={selectedCheck?.checkID}
                            filterParams={filterParams}
                        />
                    </TabPanel>
                    <TabPanel value={tabStatus} index={"Rejected"}>
                        <DataTable
                            tabStatus={tabStatus}
                            rows={rejectedRows}
                            getSelectedCheck={getSelectedCheck}
                            showLockedColumn={false}
                            showReasonColumn={true}
                            updateSortedIds={updateSortedIds}
                            selectedCheckId={selectedCheck?.checkID}
                            filterParams={filterParams}
                        />
                    </TabPanel>
                    <TabPanel value={tabStatus} index={"All"}>
                        <DataTable
                            tabStatus={tabStatus}
                            rows={allTransactions}
                            getSelectedCheck={getSelectedCheck}
                            showLockedColumn={true}
                            updateSortedIds={updateSortedIds}
                            selectedCheckId={selectedCheck?.checkID}
                            filterParams={filterParams}
                        />
                    </TabPanel>
                </Grid>
            </Box>
        </>
    );
};